import { TILE_SIZE } from '../../game/worldConstants';
import { DEFAULT_CAMERA_ZOOM, getWorldPixelSize } from '../../game/worldLayout';
import { inferImageContentType } from '../../services/firebase/mediaStoragePaths';
import { resolveLayerRenderPlane } from '../../lib/levelEditorRenderPlane';
import { getTraversalAtFromState, isColorCell } from '../../stores/editorStoreHelpers';
import type { LevelDesignDocument } from '../../types/levelEditor';
import type { SceneDocument } from '../../types/scene';
import {
  drawDesignToCanvas,
  hasRenderableLayersInPlane,
  preloadLayerImages,
} from '../components/levelEditor/rendering';
import { drawOcclusionForegroundPixels } from '../../lib/levelEditorOcclusionMask';
import {
  resolveAnimatedGifPlacements,
  UPLOADED_IMAGE_SHEET_ID,
  UPLOADED_IMAGE_TILESET_ID,
} from './levelEditorImageUpload';
import { resolveWorldLayoutFromLevelDesign } from './sceneComposerHelpers';

const THUMBNAIL_SIZE_PX = 256;

export interface RenderedSceneLevelAssets {
  animatedGifs: NonNullable<SceneDocument['animatedGifs']>;
  backgroundUrl: string;
  defaultCameraZoom: number;
  foregroundUrl: string | null;
  playerSpawnTile: { x: number; y: number };
  thumbnailUrl: string;
  traversalMaskUrl: string;
  worldLayout: SceneDocument['worldLayout'];
}

const createWorldCanvas = (width: number, height: number): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const hasSameNumber = (left: number, right: number): boolean => Math.abs(left - right) < 0.001;

const getPassthroughWebpBackgroundPath = (
  design: LevelDesignDocument,
  worldPixelSize: { width: number; height: number },
): string | null => {
  const visiblePixelLayers = design.layers.filter(
    (layer) =>
      layer.layerType !== 'animated-gif' &&
      resolveLayerRenderPlane(layer) === 'background' &&
      layer.visible &&
      layer.opacity > 0 &&
      Object.keys(layer.tiles).length > 0,
  );
  if (visiblePixelLayers.length !== 1) {
    return null;
  }

  const layer = visiblePixelLayers[0];
  if (!layer) {
    return null;
  }
  const transform = layer.uploadedImageTransform;
  if (
    !transform ||
    layer.opacity !== 1 ||
    inferImageContentType(transform.imagePath) !== 'image/webp' ||
    Math.floor(transform.offsetX) !== 0 ||
    Math.floor(transform.offsetY) !== 0 ||
    !hasSameNumber(transform.scalePercent, 100) ||
    !transform.avoidCropping ||
    Math.round(transform.imageWidth) !== worldPixelSize.width ||
    Math.round(transform.imageHeight) !== worldPixelSize.height
  ) {
    return null;
  }

  const tileEntries = Object.entries(layer.tiles);
  const expectedTileCount = design.canvasWidth * design.canvasHeight;
  if (tileEntries.length !== expectedTileCount) {
    return null;
  }

  for (let y = 0; y < design.canvasHeight; y += 1) {
    for (let x = 0; x < design.canvasWidth; x += 1) {
      const cell = layer.tiles[`${x},${y}`];
      if (
        !cell ||
        isColorCell(cell) ||
        cell.tilesetId !== UPLOADED_IMAGE_TILESET_ID ||
        cell.sheetId !== UPLOADED_IMAGE_SHEET_ID ||
        cell.imagePath !== transform.imagePath ||
        !hasSameNumber(cell.sourceX, x * TILE_SIZE) ||
        !hasSameNumber(cell.sourceY, y * TILE_SIZE) ||
        !hasSameNumber(cell.tileSize, TILE_SIZE)
      ) {
        return null;
      }
    }
  }

  return transform.imagePath;
};

const buildTraversalCanvas = (
  design: LevelDesignDocument,
  tileWidth: number,
  tileHeight: number,
): HTMLCanvasElement => {
  const canvas = createWorldCanvas(tileWidth * TILE_SIZE, tileHeight * TILE_SIZE);
  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }

  for (let y = 0; y < tileHeight; y += 1) {
    for (let x = 0; x < tileWidth; x += 1) {
      context.fillStyle =
        getTraversalAtFromState(design.layers, design.traversalOverrides, x, y) === 'walkable'
          ? '#ffffff'
          : '#000000';
      context.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
    }
  }

  return canvas;
};

const buildThumbnailCanvas = (backgroundCanvas: HTMLCanvasElement): HTMLCanvasElement => {
  const thumbnailCanvas = document.createElement('canvas');
  thumbnailCanvas.width = THUMBNAIL_SIZE_PX;
  thumbnailCanvas.height = THUMBNAIL_SIZE_PX;
  const context = thumbnailCanvas.getContext('2d');
  if (!context) {
    return thumbnailCanvas;
  }

  const scale = Math.min(
    thumbnailCanvas.width / Math.max(1, backgroundCanvas.width),
    thumbnailCanvas.height / Math.max(1, backgroundCanvas.height),
  );
  const drawWidth = Math.max(1, Math.round(backgroundCanvas.width * scale));
  const drawHeight = Math.max(1, Math.round(backgroundCanvas.height * scale));
  const drawX = Math.floor((thumbnailCanvas.width - drawWidth) / 2);
  const drawY = Math.floor((thumbnailCanvas.height - drawHeight) / 2);

  context.imageSmoothingEnabled = false;
  context.fillStyle = '#020617';
  context.fillRect(0, 0, thumbnailCanvas.width, thumbnailCanvas.height);
  context.drawImage(
    backgroundCanvas,
    0,
    0,
    backgroundCanvas.width,
    backgroundCanvas.height,
    drawX,
    drawY,
    drawWidth,
    drawHeight,
  );
  return thumbnailCanvas;
};

export const renderSceneAssetsFromLevelDesign = async (
  design: LevelDesignDocument,
): Promise<RenderedSceneLevelAssets> => {
  const worldLayout = resolveWorldLayoutFromLevelDesign(design);
  const worldPixelSize = getWorldPixelSize(worldLayout);
  const passthroughBackgroundPath = getPassthroughWebpBackgroundPath(design, worldPixelSize);
  const imageByPath = await preloadLayerImages(design.layers, {
    includeAnimatedGifLayers: false,
  });

  const backgroundCanvas = createWorldCanvas(worldPixelSize.width, worldPixelSize.height);
  drawDesignToCanvas(
    backgroundCanvas,
    design.layers,
    imageByPath,
    worldLayout.tileWidth,
    worldLayout.tileHeight,
    TILE_SIZE,
    { renderPlane: 'background' },
  );
  const hasForeground = hasRenderableLayersInPlane(design.layers, 'foreground');
  const foregroundCanvas = createWorldCanvas(worldPixelSize.width, worldPixelSize.height);
  const hasOcclusionForegroundPixels = await drawOcclusionForegroundPixels({
    backgroundCanvas,
    foregroundCanvas,
    occlusionMaskUrl: design.occlusionMaskUrl ?? null,
  });
  drawDesignToCanvas(
    foregroundCanvas,
    design.layers,
    imageByPath,
    worldLayout.tileWidth,
    worldLayout.tileHeight,
    TILE_SIZE,
    hasOcclusionForegroundPixels
      ? { clear: false, renderPlane: 'foreground' }
      : { renderPlane: 'foreground' },
  );
  const hasForegroundPixels = hasForeground || hasOcclusionForegroundPixels;
  const thumbnailSourceCanvas = hasForegroundPixels
    ? createWorldCanvas(worldPixelSize.width, worldPixelSize.height)
    : backgroundCanvas;
  if (hasForegroundPixels) {
    drawDesignToCanvas(
      thumbnailSourceCanvas,
      design.layers,
      imageByPath,
      worldLayout.tileWidth,
      worldLayout.tileHeight,
      TILE_SIZE,
      { renderPlane: 'background' },
    );
    const thumbnailContext = thumbnailSourceCanvas.getContext('2d');
    if (thumbnailContext) {
      thumbnailContext.drawImage(foregroundCanvas, 0, 0);
    }
  }

  const traversalCanvas = buildTraversalCanvas(
    design,
    worldLayout.tileWidth,
    worldLayout.tileHeight,
  );
  const thumbnailCanvas = buildThumbnailCanvas(thumbnailSourceCanvas);

  return {
    animatedGifs: resolveAnimatedGifPlacements(design.layers, TILE_SIZE),
    backgroundUrl: passthroughBackgroundPath ?? backgroundCanvas.toDataURL('image/png'),
    defaultCameraZoom: DEFAULT_CAMERA_ZOOM,
    foregroundUrl: hasForegroundPixels ? foregroundCanvas.toDataURL('image/png') : null,
    playerSpawnTile: {
      x: Math.max(0, Math.min(worldLayout.tileWidth - 1, Math.floor(design.spawnPoint.x))),
      y: Math.max(0, Math.min(worldLayout.tileHeight - 1, Math.floor(design.spawnPoint.y))),
    },
    thumbnailUrl: thumbnailCanvas.toDataURL('image/png'),
    traversalMaskUrl: traversalCanvas.toDataURL('image/png'),
    worldLayout,
  };
};
